import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import queryString from 'query-string';
import { Pagination, ChangeLog, Response } from '@ui/src';
import {
  FormValuesContent,
  UserDetails,
  UserValidator,
  SignatureType,
} from '../../types';
import { usersApi } from '../services/users';
import { Device, DeviceSession } from '../../../settings/profile/types';
import { Lock, LockAuditLog } from '../../../security/types';

export interface SidepanelUserState {
  panelIsOpen: boolean;
  userInfo?: FormValuesContent;
  validators?: UserValidator;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  devices?: Device[];
  devicesPagination?: Pagination;
  locks?: Lock[];
  locksPagination?: Pagination;
  lockItem?: Lock;
  auditLogs?: LockAuditLog[];
  auditLogsPagination?: Pagination;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  activeLock?: Lock;
  deviceSessions?: DeviceSession[];
  deviceSessionsPagination?: Pagination;
}

const initialState: SidepanelUserState = {
  panelIsOpen: false,
  userInfo: undefined,
};

export const sidepanelUserSlice = createSlice({
  name: 'sidepanelUser',
  initialState,
  reducers: {
    changePanelState: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
    },
    clearSidepanelUserState: (state) => {
      state.userInfo = undefined;
      state.panelIsOpen = false;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.devices = undefined;
      state.devicesPagination = undefined;
      state.locks = undefined;
      state.locksPagination = undefined;
      state.auditLogs = undefined;
      state.auditLogsPagination = undefined;
      state.activeLock = undefined;
    },
    clearLockPanelState: (state) => {
      state.lockItem = undefined;
      state.lockItemAuditLogs = undefined;
      state.lockItemAuditLogsPagination = undefined;
    },
    clearDeviceSession: (state) => {
      state.deviceSessions = undefined;
      state.deviceSessionsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        usersApi.endpoints.getUser.matchFulfilled,
        (state, action: PayloadAction<{ data: UserDetails }>) => {
          state.userInfo = {
            canSignTransactions: action.payload.data.canSignTransactions,
            signatureType: action.payload.data.canSignTransactions
              ? SignatureType.SINGLE
              : SignatureType.NONE,
            id: action.payload.data.id,
            firstName: action.payload.data.profile.firstName,
            lastName: action.payload.data.profile.lastName,
            avatarImg: action.payload.data.profile.avatarImg,
            email: action.payload.data.email,
            emailPrivate: action.payload.data.emailPrivate,
            employeeRoleTemplateId: action.payload.data.employeeRoleTemplateId,
            status: action.payload.data.status,
            ipsWhitelist: action.payload.data.ipsWhitelist.length
              ? action.payload.data.ipsWhitelist
              : [''],
            notificationTypeTemplateId:
              action.payload.data.notificationTypeTemplateId,
            notificationsTypes: action.payload.data.notificationsTypes,
            isCustomNotificationsTypes:
              action.payload.data.isCustomNotificationsTypes,
            totpEnabled: action?.payload?.data?.totpEnabled,
          };
        },
      )
      .addMatcher(
        usersApi.endpoints.resetNotificationTemplateToRole.matchFulfilled,
        (state, action: PayloadAction<{ data: UserDetails }>) => {
          state.userInfo = {
            ...state.userInfo,
            notificationTypeTemplateId:
              action.payload.data.notificationTypeTemplateId,
            notificationsTypes: action.payload.data.notificationsTypes,
            isCustomNotificationsTypes:
              action.payload.data.isCustomNotificationsTypes,
          };
        },
      )
      .addMatcher(
        usersApi.endpoints.resetNotificationTemplateToUserTemplate
          .matchFulfilled,
        (state, action: PayloadAction<{ data: UserDetails }>) => {
          state.userInfo = {
            ...state.userInfo,
            notificationTypeTemplateId:
              action.payload.data.notificationTypeTemplateId,
            notificationsTypes: action.payload.data.notificationsTypes,
            isCustomNotificationsTypes:
              action.payload.data.isCustomNotificationsTypes,
          };
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserChangeLogs.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.changeLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.changeLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.changeLogs = nextChangeLogs;
          }

          state.changeLogsPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserLockItem.matchFulfilled,
        (state, action: PayloadAction<{ data: Lock }>) => {
          state.lockItem = action.payload.data;
        },
      )
      .addMatcher(
        usersApi.endpoints.updateUserLock.matchFulfilled,
        (state, action: PayloadAction<{ data: Lock }>) => {
          state.locks = state.locks?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...result,
                ...action.payload.data,
              };
            }

            return result;
          });

          state.lockItem = action.payload.data;
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserDevices.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<Device>> & {
            meta: { arg: { originalArgs: { id: string; cursor?: string } } };
          },
        ) => {
          if (!action.meta.arg.originalArgs?.cursor) {
            state.devices = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextDevices = ([] as Device[]).concat(
              state.devices as Device[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextDevices.push(item.node);
            });

            state.devices = nextDevices;
          }

          state.devicesPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserValidators.matchFulfilled,
        (state, action: PayloadAction<{ data: UserValidator }>) => {
          state.validators = action.payload.data;
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserLocks.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<Lock>> & {
            meta: { arg: { originalArgs?: string } };
          },
        ) => {
          state.locks = action.payload.data.edges.map((item) => ({
            ...item.node,
            type: item.node.createdByEmployeeId ? 'by Employee' : 'Auto',
          }));

          state.locksPagination = action.payload.data.pageInfo;

          if (action.meta?.arg?.originalArgs) {
            const parsed = queryString.parse(action.meta?.arg?.originalArgs);

            if (
              parsed.locked &&
              parsed.locked === 'true' &&
              state.activeLock &&
              !action.payload.data.edges.length
            ) {
              state.activeLock = undefined;
            }
          }
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserActiveLocks.matchFulfilled,
        (state, action: PayloadAction<Response<Lock>>) => {
          if (action?.payload?.data?.edges?.length) {
            state.activeLock = action?.payload?.data?.edges[0].node;
          } else {
            state.activeLock = undefined;
          }
        },
      )
      .addMatcher(
        usersApi.endpoints.getUserAuditLogs.matchFulfilled,
        (state, action: PayloadAction<Response<LockAuditLog>>) => {
          state.auditLogs = action.payload.data.edges.map((item) => item.node);

          state.auditLogsPagination = action.payload.data.pageInfo;
        },
      );
    builder.addMatcher(
      usersApi.endpoints.getUserLockItemAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        if (state.lockItem) {
          state.lockItemAuditLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.lockItemAuditLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      usersApi.endpoints.getEmployeeDeviceSessions.matchFulfilled,
      (state, action: PayloadAction<Response<DeviceSession>>) => {
        state.deviceSessions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );

        state.deviceSessionsPagination = action.payload.data.pageInfo;
      },
    );
  },
});

export const {
  changePanelState,
  clearSidepanelUserState,
  clearLockPanelState,
  clearDeviceSession,
} = sidepanelUserSlice.actions;

export default sidepanelUserSlice.reducer;
